/**
 * Canonical SDK field projection for `useGetUser` calls that render the
 * portfolio page chrome (display name, handle, avatar + NSFW flag, hero +
 * NSFW flag, bio, connected providers, creator profile).
 *
 * Why this exists: React Query keys include the `fields:` array, so a
 * different projection per consumer means a different cache key. Both
 * `<OwnPortfolio>` and `<OtherPortfolio>` issue `useGetUser` with the same
 * intent (profile chrome) and identical field sets — extracting the constant
 * keeps them in lockstep so any future field addition lands in one place,
 * and ensures cross-route warm-cache reuse (e.g. drilling from
 * `/portfolio/<id>` back into `/portfolio` shares the cache entry when the
 * target user matches).
 */
export const PORTFOLIO_USER_PROFILE_FIELDS = [
  'userId',
  'createdAt',
  // The target's primary wallet pubkey — `<OtherPortfolio>` reads it as the
  // single-wallet realtime-subject / REST fallback when `wallets[]` is empty.
  // Without it that read is always `undefined` and the fallback degrades to the
  // URL identifier (a UUID), which then base58-decode-crashes the positions
  // endpoints.
  'pubkey',
  'displayName',
  'handle',
  'avatarUrl',
  'avatarNsfw',
  'avatarPreferences',
  'heroUrl',
  'heroNsfw',
  'bio',
  'connectedProviders',
  // `wallets[]` — every wallet pubkey the target user owns. Threaded
  // back into per-user-feed hooks as `walletPubkeys:` so realtime
  // subscriptions cover EVERY wallet (a single-wallet subscription
  // silently misses the other wallets' chain events).
  'wallets',
] as const;

/** Follower / following identity rows surfaced by the followers dialog. */
export const FOLLOWS_LIST_FIELDS = ['user'] as const;

/** Unified activity feed projection — covers every kind the v2
 *  `useGetUserActivity` route emits (trade / claim / back / unback /
 *  boost / gift_sent / gift_received). */
export const ACTIVITY_FEED_FIELDS = [
  'marketType',
  'partnerCreatorId',
  'associatedCreator',
  'kind',
  'ts',
  'id',
  'market',
  'amountD6',
  'outcomeIndex',
  'side',
  'priceD9',
  'tradeEventId',
  'giftPda',
  'participationId',
  'opaqueId',
  'counterpartyHandle',
  'giftStatus',
  'assetKind',
  'trader',
  'outcomeLabel',
  'marketTitle',
  'marketCoverImageUrl',
  'marketCoverImageNsfw',
] as const;

/**
 * Fields consumed by the portfolio Theses tab. `useGetThesesInfinite` now
 * returns the complete fixed EnrichedThesis DTO; this array is not sent on
 * the wire.
 */
export const USER_THESES_FIELDS = [
  'signature',
  'marketPubkey',
  'outcomeIndex',
  'outcomeLabel',
  'outcomeImageUrl',
  'outcomeImageNsfw',
  'content',
  'createdAt',
  'rating',
  'replyCount',
  'viewerReaction',
  'tradeDirection',
  'tradeAmount',
  'marketTitle',
  'marketCategory',
  'marketStatus',
  'trader',
] as const;

/** Canonical positions projection — drives the shared `useMarketPositions`
 *  hook (PnL card + trade form, MOR-852) and the resolved-state devtool
 *  applier. One fields list ⇒ one cache entry + one realtime subscription
 *  set per market scope. */
export const POSITION_FIELDS = [
  'marketType',
  'partnerCreatorId',
  'associatedCreator',
  'positionId',
  'positionType',
  'wallet',
  'market',
  'marketTitle',
  'marketStatus',
  'outcomeIndex',
  'outcomeMint',
  'outcomeLabel',
  'outcomeImageUrl',
  'outcomeImageNsfw',
  'balance',
  'claimRightsBalanceD6',
  'avgEntryPriceD9',
  'currentPriceD9',
  'costBasisD6',
  'remainingCostBasisGrossD6',
  'remainingCostBasisNetD6',
  'realizedPnlGrossD6',
  'realizedPnlNetD6',
  'realizedPenaltyD6',
  'realizedPnlD6',
  'realizedCostBasisGrossD6',
  'realizedCostBasisNetD6',
  'realizedCostBasisD6',
  'unrealizedPnlD6',
  'tradeCount',
  'isOpen',
  'resultState',
  'prizeClaimableD6',
  'prizeClaimedD6',
  'prizeState',
  'pnl',
  'pnlUnavailableReason',
] as const;
